import { z } from 'zod';
import { localToUtcIso } from '../utils/session-datetime';

// Reusable atomic constraints
const titleSchema = z
  .string({ required_error: 'El título es obligatorio' })
  .trim()
  .min(3, 'El título debe tener al menos 3 caracteres')
  .max(255, 'El título no puede exceder 255 caracteres');

const descriptionSchema = z
  .string()
  .trim()
  .max(2000, 'La descripción no puede exceder 2000 caracteres')
  .nullable()
  .optional()
  .transform(v => (v && v.length > 0 ? v : null));

const dateSchema = z
  .string({ required_error: 'La fecha es obligatoria' })
  .min(1, 'La fecha es obligatoria')
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Formato de fecha inválido');

const timeSchema = z
  .string({ required_error: 'La hora es obligatoria' })
  .min(1, 'La hora es obligatoria')
  .regex(/^\d{2}:\d{2}$/, 'Formato de hora inválido');

const locationSchema = z
  .string()
  .trim()
  .max(255, 'La ubicación no puede exceder 255 caracteres')
  .nullable()
  .optional()
  .transform(v => (v && v.length > 0 ? v : null));

/**
 * Form schema for create / edit. A session happens on a single date with
 * two times (start + end); `endDateTime` is derived in the submit handler by
 * combining `startDate` with `endTime`.
 *
 * Instructor: either a system user (uuid) OR a free-text external name — at
 * least one must be present.
 */
export const sessionFormSchema = z
  .object({
    title: titleSchema,
    description: descriptionSchema,
    startDate: dateSchema,
    startTime: timeSchema,
    endTime: timeSchema,
    instructorUserId: z.string().uuid().nullable().optional().or(z.literal('')).transform(v => (v && v.length > 0 ? v : null)),
    instructorName: z.string().trim().nullable().optional().transform(v => (v && v.length > 0 ? v : null)),
    location: locationSchema,
  })
  .superRefine((data, ctx) => {
    const startIso = localToUtcIso(data.startDate, data.startTime);
    const endIso = localToUtcIso(data.startDate, data.endTime);
    if (startIso && endIso && new Date(endIso) <= new Date(startIso)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'La hora de fin debe ser posterior a la de inicio',
        path: ['endTime'],
      });
    }
    if (!data.instructorUserId && !data.instructorName) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Debe especificar un instructor (usuario del sistema o nombre externo)',
        path: ['instructorName'],
      });
    }
  });

export type SessionFormValues = z.infer<typeof sessionFormSchema>;

export const sessionFormDefaultValues: SessionFormValues = {
  title: '',
  description: null,
  startDate: '',
  startTime: '',
  endTime: '',
  instructorUserId: null,
  instructorName: null,
  location: null,
};
